import traceUFOPress from '@atlassian/react-ufo/trace-press';
import { colors } from '@atlaskit/theme';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Dimensions } from 'components/visuals';
import { State } from 'model/State';

import {
  fetchIsSiteOnConcession,
  fetchTrackedElsewhere,
  fetchIsOnBehalfOfEmail,
  fetchIsHostedAccountProvisioning,
} from '../duck/actions/accountId';
import {
  fetchDetails,
  fetchIsRenewableAccount,
  fetchIsUpgradableAccount,
} from '../duck/actions/accounts';
import { MACTableType } from './TableContent';
import LicensesExpandedRow from '../components/licensesExpandedRow/licensesExpandedRow';
import OrdersExpandedRow from '../components/OrdersExpandedRow';
import { LicensesCollapsedRow } from '../components/LicensesCollapsedRow';
import { InvoiceCollapsedRow } from '../components/InvoiceCollapsedRow';
import { OrderCollapsedRow } from '../components/OrderCollapsedRow';
import { fetchLicenseById } from '../duck/actions/license';
import TrialLicenseExpandedRow from '../components/TrialLicenseExpandedRow';
import {
  LICENSE_DETAILS_UFO_NAME,
  LICENSE_IS_HOSTED_ACCOUNT_PROVISIONING_FETCH_UFO_NAME,
  LICENSE_IS_ON_BEHALF_OF_EMAIL_FETCH_UFO_NAME,
  LICENSE_IS_RENEWABLE_ACCOUNT_FETCH_UFO_NAME,
  LICENSE_IS_SITE_ON_CONCESSION_FETCH_UFO_NAME,
  LICENSE_IS_UPGRADABLE_ACCOUNT_FETCH_UFO_NAME,
  LICENSE_TRACKED_ELSEWHERE_FETCH_UFO_NAME,
} from 'util/ufoNameConstants';

const CollapseSectionWrapper = styled.tr<{ $isArchived?: boolean }>`
  margin-bottom: ${Dimensions.units(1)}px;
  height: ${Dimensions.units(4)}px;
  padding: ${Dimensions.units(0.5)}px ${Dimensions.units(0.5)}px;
  cursor: pointer;
  border-top: solid 2px ${colors.N30};
  ${({ $isArchived }) => $isArchived && `background-color: ${colors.N40};`}
`;

const ChildSection = styled.tr<{ isSectionVisible: boolean }>`
  visibility: ${(props) => (props.isSectionVisible ? 'visible' : 'collapse')};
  height: ${(props) => (props.isSectionVisible ? 'unset' : '0')};
  background-color: ${colors.N20};
  vertical-align: top !important;
`;

interface Props {
  rowItem: any;
  tableType: MACTableType;
  senNumber?: string;
  isCollapsedAll?: boolean;
  isPartner?: boolean;
}

export const CollapseRow: React.FC<Props> = ({
  rowItem,
  tableType,
  senNumber,
  isPartner,
  isCollapsedAll,
}) => {
  const hash = useSelector((state: State) => state.location.query?.hash);
  const editedUuid = useSelector((state: State) => state.location.query?.editedUuid);

  const isArchived = rowItem?.archived;
  const isTrial = rowItem?.licenseType === 'EVALUATION';

  const isLicensesSenPreselected =
    tableType === MACTableType.licenses && senNumber && rowItem.accountId === +senNumber;
  const isTrialLicensesSenPreselected =
    tableType === MACTableType.licenses &&
    senNumber &&
    rowItem.licenseId?.toString() === senNumber?.replace('L', '');
  const isQuoteIdPreselected =
    (tableType === MACTableType.invoices || tableType === MACTableType.orders) &&
    senNumber &&
    rowItem.orderId === +senNumber;
  const isOrderPreselected =
    editedUuid && tableType === MACTableType.orders && rowItem.uuid === editedUuid;
  const isSENPreselected =
    isLicensesSenPreselected ||
    isQuoteIdPreselected ||
    isTrialLicensesSenPreselected ||
    isOrderPreselected;

  const [showContent, setShowContent] = useState<boolean>(isSENPreselected || false);
  const lineHeader = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isCollapsedAll) {
      setShowContent(false);
    }
  }, [isCollapsedAll]);

  useLayoutEffect(() => {
    if (isSENPreselected && lineHeader.current) {
      (lineHeader.current as Element).scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (isTrialLicensesSenPreselected) {
      dispatch(fetchLicenseById(rowItem.licenseId));
    }
  }, [isTrialLicensesSenPreselected]);

  const onSectionToggle = () => {
    if (!showContent) {
      if (tableType === MACTableType.licenses) {
        traceUFOPress(LICENSE_DETAILS_UFO_NAME);
      }

      if (rowItem?.licenseId) {
        dispatch(fetchLicenseById(rowItem.licenseId, LICENSE_DETAILS_UFO_NAME));
      } else {
        const id = rowItem.parentAccountId || rowItem.accountId;
        dispatch(fetchDetails(id, LICENSE_DETAILS_UFO_NAME));
        traceUFOPress(LICENSE_TRACKED_ELSEWHERE_FETCH_UFO_NAME);
        dispatch(fetchTrackedElsewhere(id, LICENSE_TRACKED_ELSEWHERE_FETCH_UFO_NAME));
        traceUFOPress(LICENSE_IS_SITE_ON_CONCESSION_FETCH_UFO_NAME);
        dispatch(fetchIsSiteOnConcession(id, LICENSE_IS_SITE_ON_CONCESSION_FETCH_UFO_NAME));
        traceUFOPress(LICENSE_IS_RENEWABLE_ACCOUNT_FETCH_UFO_NAME);
        dispatch(fetchIsRenewableAccount(id, LICENSE_IS_RENEWABLE_ACCOUNT_FETCH_UFO_NAME));
        traceUFOPress(LICENSE_IS_UPGRADABLE_ACCOUNT_FETCH_UFO_NAME);
        dispatch(fetchIsUpgradableAccount(id, LICENSE_IS_UPGRADABLE_ACCOUNT_FETCH_UFO_NAME));
        traceUFOPress(LICENSE_IS_ON_BEHALF_OF_EMAIL_FETCH_UFO_NAME);
        dispatch(fetchIsOnBehalfOfEmail(id, LICENSE_IS_ON_BEHALF_OF_EMAIL_FETCH_UFO_NAME));
        traceUFOPress(LICENSE_IS_HOSTED_ACCOUNT_PROVISIONING_FETCH_UFO_NAME);
        dispatch(
          fetchIsHostedAccountProvisioning(
            id,
            LICENSE_IS_HOSTED_ACCOUNT_PROVISIONING_FETCH_UFO_NAME,
          ),
        );
      }
    }

    setShowContent((prevState) => !prevState);
  };

  return (
    <>
      <CollapseSectionWrapper
        onClick={onSectionToggle}
        data-cy-mac-table-row
        $isArchived={isArchived}
        ref={lineHeader}
      >
        {tableType === MACTableType.orders && (
          <OrderCollapsedRow
            showContent={showContent}
            rowItem={rowItem}
            isPartner={isPartner}
            urlOrderId={senNumber}
            hash={hash}
          />
        )}
        {tableType === MACTableType.invoices && (
          <InvoiceCollapsedRow showContent={showContent} rowItem={rowItem} />
        )}
        {tableType === MACTableType.licenses && (
          <LicensesCollapsedRow
            showContent={showContent}
            rowItem={rowItem}
            isTrial={isTrial}
            isPartner={isPartner}
          />
        )}
      </CollapseSectionWrapper>
      <ChildSection isSectionVisible={showContent}>
        {tableType === MACTableType.licenses &&
          (rowItem?.licenseId ? (
            <TrialLicenseExpandedRow licenseId={rowItem.licenseId} isArchived={rowItem.archived} />
          ) : (
            <LicensesExpandedRow
              sen={rowItem.accountId || rowItem.licenseId}
              isAccount={!!rowItem.accountId}
              isPartner={isPartner}
              isArchived={rowItem.archived}
            />
          ))}
        {(tableType === MACTableType.orders || tableType === MACTableType.invoices) && (
          <OrdersExpandedRow key={rowItem.id} rowItem={rowItem} tableType={tableType} />
        )}
      </ChildSection>
    </>
  );
};
